/**
 * compactLivePreview — derives the live SQL text of the cursor statement for
 * the Compact VQE popover's collapsible preview. The buffer is the source of
 * truth, so the preview is just a slice of the current buffer over the
 * statement's offset range — it always reflects the latest committed edit.
 *
 * Pure logic so it's unit-testable without Monaco. Offsets come from the
 * Resolver `StatementInfo.range` (UTF-16 code-unit offsets, matching the
 * buffer string), threaded down as a plain `{ start, end }` span.
 *
 * spec: docs/superpowers/specs/2026-06-14-vqe-ux-refinement-design.md § WS3
 *   (Footer — live SQL preview).
 */

export interface OffsetSpan {
  readonly start: number;
  readonly end: number;
}

/**
 * Slice the statement text out of the full buffer. Clamps the span to the
 * buffer bounds and trims a trailing statement terminator + surrounding
 * whitespace so the preview reads cleanly. Falls back to the whole (trimmed)
 * buffer when no span is available.
 */
export function buildCompactPreview(buffer: string, span: OffsetSpan | null): string {
  if (!span) return buffer.trim();
  const start = Math.max(0, Math.min(span.start, buffer.length));
  const end = Math.max(start, Math.min(span.end, buffer.length));
  const slice = buffer.slice(start, end).trim();
  // Drop a single trailing ';' so the preview shows just the statement body.
  return slice.endsWith(';') ? slice.slice(0, -1).trimEnd() : slice;
}
